import { State } from "@/network/state";
import { Request } from "@/network/request";
import { ref } from "vue";
import { canPermission } from "@/abilities/ability";
import { Repo } from "@/store/admin/repo";

const POLL_MS = 45000;
const DEFAULT_PER_PAGE = 15;
const LIST_PERMISSION = "dashboard.notification.list";
const BADGE_PERMISSION = "dashboard.notification.unreadCount";
const READ_PERMISSION = "dashboard.notification.read";

export const unreadCount = ref(0);
export const listItems = ref([]);
export const headerItems = ref([]);
export const listMeta = ref(null);
export const headerMeta = ref(null);
export const headerLoading = ref(false);
export const headerLoadingMore = ref(false);
export const listLoading = ref(false);
export const markAllLoading = ref(false);

const pickUnreadCount = (payload) => {
  if (payload == null || typeof payload !== "object") return null;
  if (payload.unread_count != null) return payload.unread_count;
  if (payload.un_read_count != null) return payload.un_read_count;
  if (payload.data?.unread_count != null) return payload.data.unread_count;
  if (payload.data?.un_read_count != null) return payload.data.un_read_count;
  return null;
};

class notification {
  endPoint = "/dashboard/notification";
  getState = new State();
  headerState = new State();
  badgeState = new State();
  readState = new State();
  pollTimer = null;

  isStaff() {
    const type = Repo?.user?.type;
    return type === "ADMIN" || type === "EMPLOYEE";
  }

  canList() {
    return this.isStaff() && canPermission(LIST_PERMISSION);
  }

  canBadge() {
    return this.isStaff() && canPermission(BADGE_PERMISSION);
  }

  canMarkRead() {
    return this.isStaff() && canPermission(READ_PERMISSION);
  }

  applyUnreadCount(value) {
    const count = Number(value);
    unreadCount.value = Number.isFinite(count) ? count : 0;
  }

  markItemsRead(itemsRef) {
    if (!Array.isArray(itemsRef.value)) return;
    itemsRef.value = itemsRef.value.map((item) =>
      item ? { ...item, is_read: true } : item
    );
  }

  async get({ params, onSuccess = () => {}, onError = () => {} } = {}) {
    const query = { per_page: DEFAULT_PER_PAGE, ...params };
    let request = new Request({
      method: "get",
      endpoint: `${this.endPoint}`,
      params: query,
    });
    listLoading.value = true;
    await this.getState.trigger(
      async () => {
        return await request.send();
      },
      {
        onSuccess: (value) => {
          listItems.value = Array.isArray(value?.data) ? value.data : [];
          listMeta.value = value?.meta || null;
          const count = pickUnreadCount(value);
          if (count != null) this.applyUnreadCount(count);
          onSuccess(value);
        },
        onError: () => {
          onError();
        },
      }
    );
    listLoading.value = false;
  }

  async getHeader({
    params,
    append = false,
    onSuccess = () => {},
    onError = () => {},
  } = {}) {
    const query = { per_page: DEFAULT_PER_PAGE, page: 1, ...params };
    const page = Number(query.page) || 1;
    let request = new Request({
      method: "get",
      endpoint: `${this.endPoint}`,
      params: query,
    });

    if (append) {
      headerLoadingMore.value = true;
    } else {
      headerLoading.value = true;
    }

    await this.headerState.trigger(
      async () => {
        return await request.send();
      },
      {
        onSuccess: (value) => {
          const rows = Array.isArray(value?.data) ? value.data : [];
          if (append && page > 1) {
            const existingIds = new Set(
              headerItems.value.map((item) => item?.id)
            );
            headerItems.value = [
              ...headerItems.value,
              ...rows.filter((item) => !existingIds.has(item?.id)),
            ];
          } else {
            headerItems.value = rows;
          }
          headerMeta.value = value?.meta || null;
          const count = pickUnreadCount(value);
          if (count != null) this.applyUnreadCount(count);
          onSuccess(value);
        },
        onError: () => {
          onError();
        },
      }
    );

    if (append) {
      headerLoadingMore.value = false;
    } else {
      headerLoading.value = false;
    }
  }

  async loadMoreHeader({ per_page = DEFAULT_PER_PAGE } = {}) {
    if (!this.canList()) return;
    if (headerLoading.value || headerLoadingMore.value) return;

    const meta = headerMeta.value || {};
    const current = Number(meta.current_page || 1);
    const last = Number(meta.last_page || 1);
    if (current >= last) return;

    await this.getHeader({
      params: { page: current + 1, per_page },
      append: true,
    });
  }

  async markAllRead({ onSuccess = () => {}, onError = () => {} } = {}) {
    if (!this.canMarkRead()) return;

    markAllLoading.value = true;
    let request = new Request({
      method: "get",
      endpoint: `${this.endPoint}/read`,
      params: { read_all: true },
    });

    await this.readState.trigger(
      async () => {
        return await request.send();
      },
      {
        onSuccess: (value) => {
          const count = pickUnreadCount(value?.data) ?? pickUnreadCount(value);
          if (count != null) this.applyUnreadCount(count);
          else this.applyUnreadCount(0);
          this.markItemsRead(listItems);
          this.markItemsRead(headerItems);
          onSuccess(value);
        },
        onError: () => {
          onError();
        },
      }
    );
    markAllLoading.value = false;
  }

  async refreshBadge() {
    if (!this.canBadge()) {
      unreadCount.value = 0;
      return;
    }

    let request = new Request({
      method: "get",
      endpoint: `${this.endPoint}/unread-count`,
    });

    await this.badgeState.trigger(
      async () => {
        return await request.send();
      },
      {
        onSuccess: (value) => {
          const count = pickUnreadCount(value?.data) ?? pickUnreadCount(value);
          if (count != null) this.applyUnreadCount(count);
        },
      }
    );
  }

  onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.refreshBadge();
    }
  };

  startPolling() {
    this.stopPolling();
    if (!this.canBadge()) return;
    this.refreshBadge();
    this.pollTimer = setInterval(() => {
      if (document.visibilityState === "visible") {
        this.refreshBadge();
      }
    }, POLL_MS);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }
}

export const Notification = new notification();
export const NOTIFICATION_PER_PAGE = DEFAULT_PER_PAGE;
